// Stateful log analyzer: encounter detection, death tracking and segment
// orchestration on top of the parser and the aggregator.
//
// The analyzer never holds the raw event stream. Everything it keeps is either
// an incremental aggregate or an explicitly bounded buffer (recent events per
// player, capped death details), so a 400 MB log costs the same memory as a
// 40 MB one.

import { createParser, parseLogFileName } from './parser'
import { createRegistry } from './entities'
import {
    createSegment,
    applyEvent,
    applyAura,
    closeOpenAuras,
    segmentStats,
} from './aggregator'
import { unquote, parseGrouped } from './tokenizer'
import { describeDifficulty, describeDefensive, INSTANCE_DIFFICULTY_IDS } from './constants'

// Bumped when the shape of what a snapshot carries changes. Version 4 adds the
// defensive cooldown entries to the per player death window.
export const STATE_VERSION = 4

const SWING_DEDUPE_WINDOW_MS = 250
const RECENT_EVENTS_PER_PLAYER = 220
const DEATH_WINDOW_MS = 10000
const MAX_DETAILED_DEATHS = 300
const MAX_UNKNOWN_EVENTS = 60

function createPlayerTrack() {
    return {
        hp: 0,
        maxHp: 0,
        hpTs: 0,
        auras: new Map(),
        recent: new Array(RECENT_EVENTS_PER_PLAYER),
        recentNext: 0,
        recentCount: 0,
    }
}

function pushRecent(track, record) {
    const slot = track.recentNext
    track.recent[slot] = record
    track.recentNext = (slot + 1) % RECENT_EVENTS_PER_PLAYER
    if (track.recentCount < RECENT_EVENTS_PER_PLAYER) track.recentCount += 1
}

function collectRecent(track, fromTs) {
    const out = []
    for (let step = 1; step <= track.recentCount; step += 1) {
        const index = (track.recentNext - step + RECENT_EVENTS_PER_PLAYER) % RECENT_EVENTS_PER_PLAYER
        const record = track.recent[index]
        if (!record || record.ts < fromTs) break
        out.push(record)
    }
    return out.reverse()
}

export function createAnalyzer(options = {}) {
    const fileName = options.fileName || ''
    const fileNameHint = parseLogFileName(fileName)
    const registry = createRegistry(options.registry)
    const parser = createParser({ fileNameHint, dateOrder: options.dateOrder })

    const state = {
        version: STATE_VERSION,
        fileName,
        offset: 0,
        size: 0,
        lastModified: 0,
        headSignature: '',
        tailSignature: '',
        dateOrder: options.dateOrder || '',
        logVersion: 0,
        buildVersion: '',
        advancedLogging: false,
        firstTs: 0,
        lastTs: 0,
        lineCount: 0,
        eventCount: 0,
        skippedLines: 0,
        zone: null,
        zones: new Map(),
        segmentCounter: 0,
        deaths: [],
        deathCount: 0,
        enemyDeaths: 0,
        unknownEvents: new Map(),
        parseErrors: 0,
    }

    const tracks = new Map()
    let logSegment = createSegment({ key: 'log', type: 'log', name: fileName, startTs: 0, bucketMs: 4000 })
    let activeChallenge = null
    let activeEncounter = null
    let finishedSegments = []

    // Swing damage is logged twice: once from the attacker's point of view
    // (SWING_DAMAGE) and once from the victim's (SWING_DAMAGE_LANDED). Both rows
    // describe the same hit, so the hit is counted once.
    //
    // A row only ever cancels a row of the *other* kind, which matters because
    // two genuine main hand / off hand swings for the same amount inside the
    // matching window are real, separate hits and both have to count. Not every
    // row is paired either (roughly 2% of LANDED rows arrive without their
    // SWING_DAMAGE), so an unpaired row is counted on its own.
    let pendingSwing = new Map()
    let pendingLanded = new Map()
    let dedupePrunedAt = 0

    function pruneSwingPairs(ts) {
        if (ts - dedupePrunedAt < 500) return
        dedupePrunedAt = ts
        const cutoff = ts - SWING_DEDUPE_WINDOW_MS
        const prune = (map) => {
            map.forEach((stamps, key) => {
                const kept = stamps.filter((stamp) => stamp >= cutoff)
                if (kept.length) map.set(key, kept)
                else map.delete(key)
            })
        }
        prune(pendingSwing)
        prune(pendingLanded)
    }

    function isDuplicateSwing(event) {
        const landed = event.name === 'SWING_DAMAGE_LANDED'
        const key = event.sourceGuid + '|' + event.destGuid + '|' + event.amount + '|' + event.absorbed
        const counterpart = landed ? pendingSwing : pendingLanded
        const own = landed ? pendingLanded : pendingSwing

        const waiting = counterpart.get(key)
        if (waiting && waiting.length) {
            const index = waiting.findIndex((stamp) => Math.abs(event.ts - stamp) <= SWING_DEDUPE_WINDOW_MS)
            if (index >= 0) {
                waiting.splice(index, 1)
                if (!waiting.length) counterpart.delete(key)
                return true
            }
        }

        const mine = own.get(key)
        if (mine) mine.push(event.ts)
        else own.set(key, [event.ts])
        return false
    }

    function track(guid) {
        let entry = tracks.get(guid)
        if (!entry) {
            entry = createPlayerTrack()
            tracks.set(guid, entry)
        }
        return entry
    }

    function activeSegments() {
        if (activeEncounter && activeChallenge) return [logSegment, activeChallenge, activeEncounter]
        if (activeEncounter) return [logSegment, activeEncounter]
        if (activeChallenge) return [logSegment, activeChallenge]
        return [logSegment]
    }

    function segmentKeys() {
        const keys = ['log']
        if (activeChallenge) keys.push(activeChallenge.key)
        if (activeEncounter) keys.push(activeEncounter.key)
        return keys
    }

    function openSegment(type, name, meta, ts) {
        state.segmentCounter += 1
        return createSegment({
            key: type + ':' + state.segmentCounter,
            type,
            name,
            startTs: ts,
            bucketMs: 1000,
            meta,
        })
    }

    function closeSegment(segment, endTs, extraMeta) {
        if (!segment) return
        segment.endTs = endTs || segment.lastTs
        if (extraMeta) Object.assign(segment.meta, extraMeta)
        closeOpenAuras(segment, segment.endTs)
        finishedSegments.push(segment)
    }

    function setZone(zone, ts) {
        const key = zone.instanceId + ':' + zone.name + ':' + zone.difficultyId
        let entry = state.zones.get(key)
        if (!entry) {
            entry = { ...zone, key, firstTs: ts, lastTs: ts, events: 0, visits: 0 }
            state.zones.set(key, entry)
        }
        entry.visits += 1
        entry.lastTs = ts
        state.zone = entry
    }

    function handleMeta(event) {
        const fields = event.fields
        switch (event.name) {
            case 'COMBAT_LOG_VERSION':
                state.logVersion = Number(fields[1]) || 0
                state.advancedLogging = fields[3] === '1'
                state.buildVersion = unquote(fields[5] || '')
                break

            case 'ZONE_CHANGE':
                setZone({
                    instanceId: Number(fields[1]) || 0,
                    name: unquote(fields[2]),
                    difficultyId: Number(fields[3]) || 0,
                    difficulty: describeDifficulty(fields[3]),
                }, event.ts)
                break

            case 'MAP_CHANGE':
                if (!state.zone || !state.zone.name) {
                    setZone({
                        instanceId: 0,
                        uiMapId: Number(fields[1]) || 0,
                        name: unquote(fields[2]),
                        difficultyId: 0,
                        difficulty: '',
                    }, event.ts)
                }
                break

            case 'CHALLENGE_MODE_START': {
                if (activeChallenge) closeSegment(activeChallenge, event.ts, { completed: false })
                const affixes = parseGrouped(fields[5] || '')
                activeChallenge = openSegment('challenge', unquote(fields[1]), {
                    zoneName: unquote(fields[1]),
                    instanceId: Number(fields[2]) || 0,
                    challengeModeId: Number(fields[3]) || 0,
                    keystoneLevel: Number(fields[4]) || 0,
                    affixIds: Array.isArray(affixes) ? affixes.map(Number).filter(Boolean) : [],
                    completed: false,
                    success: null,
                }, event.ts)
                break
            }

            case 'CHALLENGE_MODE_END': {
                // The client also emits CHALLENGE_MODE_END with zeroed fields when
                // a keystone instance is entered. Without an open run there is
                // nothing to close; with one open, a zeroed row (or one naming a
                // different instance) means the run was left, not failed, so the
                // result stays unknown instead of being reported as a wipe.
                if (!activeChallenge) break
                const endInstanceId = Number(fields[1]) || 0
                const totalTimeMs = Number(fields[4]) || 0
                const resultKnown = totalTimeMs > 0
                    && (!endInstanceId || endInstanceId === activeChallenge.meta.instanceId)

                if (!resultKnown) {
                    closeSegment(activeChallenge, event.ts, { completed: false, success: null, abandoned: true })
                    activeChallenge = null
                    break
                }

                closeSegment(activeChallenge, event.ts, {
                    completed: true,
                    success: fields[2] === '1',
                    keystoneLevel: Number(fields[3]) || activeChallenge.meta.keystoneLevel || 0,
                    totalTimeMs,
                    runRating: fields[5] !== undefined ? Number(fields[5]) || 0 : null,
                    overallScore: fields[6] !== undefined ? Number(fields[6]) || 0 : null,
                })
                activeChallenge = null
                break
            }

            case 'ENCOUNTER_START': {
                if (activeEncounter) closeSegment(activeEncounter, event.ts, { completed: false })
                activeEncounter = openSegment('encounter', unquote(fields[2]), {
                    encounterId: Number(fields[1]) || 0,
                    difficultyId: Number(fields[3]) || 0,
                    difficulty: describeDifficulty(fields[3]),
                    groupSize: Number(fields[4]) || 0,
                    instanceId: Number(fields[5]) || 0,
                    completed: false,
                    success: null,
                }, event.ts)
                break
            }

            case 'ENCOUNTER_END': {
                if (!activeEncounter) break
                closeSegment(activeEncounter, event.ts, {
                    completed: true,
                    success: fields[5] === '1',
                    fightTimeMs: Number(fields[6]) || 0,
                })
                activeEncounter = null
                break
            }

            case 'COMBATANT_INFO': {
                const actor = registry.applyCombatantInfo(fields)
                if (actor) {
                    const itemLevel = registry.readCombatantItemLevel(fields)
                    if (itemLevel) actor.itemLevel = itemLevel
                    activeSegments().forEach((segment) => {
                        segment.roster.set(actor.guid, {
                            guid: actor.guid,
                            specId: actor.specId,
                            className: actor.className,
                            spec: actor.spec,
                            role: actor.role,
                            itemLevel: actor.itemLevel,
                        })
                    })
                }
                break
            }

            default:
                break
        }
    }

    function updateHp(event, actor) {
        if (!event.hasAdvanced || !event.infoGuid) return
        const infoActor = registry.get(event.infoGuid)
        if (!infoActor || !infoActor.isPlayer) return
        const entry = track(event.infoGuid)
        entry.hp = event.currentHp
        entry.maxHp = event.maxHp
        entry.hpTs = event.ts
        if (event.maxHp > infoActor.maxHp) infoActor.maxHp = event.maxHp
        if (event.level && !infoActor.itemLevel) infoActor.itemLevel = event.level
        if (actor) return
    }

    function recordDeath(event, actor) {
        const entry = track(actor.guid)
        const recent = collectRecent(entry, event.ts - DEATH_WINDOW_MS)
        const killingBlow = [...recent].reverse().find((record) => record.kind === 'damage' && record.amount > 0) || null
        const auras = []
        entry.auras.forEach((aura) => {
            auras.push({
                spellId: aura.spellId,
                name: aura.name,
                auraType: aura.auraType,
                stacks: aura.stacks,
                appliedAt: aura.appliedAt,
            })
        })

        const detailed = state.deaths.length < MAX_DETAILED_DEATHS
        const death = {
            guid: actor.guid,
            name: actor.shortName || actor.name,
            ts: event.ts,
            segmentKeys: segmentKeys(),
            encounterName: activeEncounter ? activeEncounter.name : (activeChallenge ? activeChallenge.name : ''),
            hp: entry.hp,
            maxHp: entry.maxHp,
            killingBlow,
            events: detailed ? recent : [],
            activeAuras: detailed ? auras : [],
            windowMs: DEATH_WINDOW_MS,
        }

        state.deaths.push(death)
        state.deathCount += 1

        activeSegments().forEach((segment) => {
            const stats = segmentStats(segment, actor.guid)
            stats.deaths += 1
            segment.totals.deaths += 1
            segment.deaths.push(state.deaths.length - 1)
        })
    }

    function trackPlayerEvent(event) {
        const dest = event.destGuid ? registry.get(event.destGuid) : null

        if (event.category === 'damage' && dest && dest.isPlayer) {
            const entry = track(dest.guid)
            const hpAfter = event.hasAdvanced && event.infoGuid === dest.guid ? event.currentHp : entry.hp
            const maxHp = event.hasAdvanced && event.infoGuid === dest.guid ? event.maxHp : entry.maxHp
            pushRecent(entry, {
                ts: event.ts,
                kind: 'damage',
                sourceName: event.environmentalType || event.sourceName || 'Unknown',
                spellId: event.spellId,
                spellName: event.spellName || event.environmentalType || 'Melee',
                amount: event.amount,
                absorbed: event.absorbed,
                overkill: event.overkill,
                critical: event.critical,
                hpAfter,
                maxHp,
            })
            return
        }

        if (event.category === 'heal' && dest && dest.isPlayer) {
            const entry = track(dest.guid)
            const effective = Math.max(0, event.amount - event.overheal)
            const hpAfter = event.hasAdvanced && event.infoGuid === dest.guid ? event.currentHp : entry.hp
            pushRecent(entry, {
                ts: event.ts,
                kind: 'heal',
                sourceName: event.sourceName || 'Unknown',
                spellId: event.spellId,
                spellName: event.spellName,
                amount: effective,
                overheal: event.overheal,
                critical: event.critical,
                hpAfter,
                maxHp: entry.maxHp,
            })
            return
        }

        if (event.category === 'absorbed' && dest && dest.isPlayer && event.amount > 0) {
            const entry = track(dest.guid)
            pushRecent(entry, {
                ts: event.ts,
                kind: 'absorb',
                sourceName: event.absorbCasterName || '',
                spellId: event.extraSpellId,
                spellName: event.extraSpellName,
                amount: event.amount,
                hpAfter: entry.hp,
                maxHp: entry.maxHp,
            })
        }
    }

    function trackAura(event) {
        const dest = registry.get(event.destGuid)
        if (!dest || !dest.isPlayer) return
        const entry = track(dest.guid)

        // A known defensive going up or down belongs in the death window next
        // to the damage it was meant to answer.
        const defensive = describeDefensive(event.spellId)
        if (defensive && (event.name === 'SPELL_AURA_APPLIED' || event.name === 'SPELL_AURA_REMOVED')) {
            pushRecent(entry, {
                ts: event.ts,
                kind: 'defensive',
                applied: event.name === 'SPELL_AURA_APPLIED',
                defensiveKind: defensive.kind,
                sourceName: event.sourceName || '',
                selfCast: event.sourceGuid === dest.guid,
                spellId: event.spellId,
                spellName: event.spellName || defensive.name,
                amount: 0,
                hpAfter: entry.hp,
                maxHp: entry.maxHp,
            })
        }

        if (event.name === 'SPELL_AURA_REMOVED') {
            entry.auras.delete(event.spellId)
        } else if (
            event.name === 'SPELL_AURA_APPLIED'
            || event.name === 'SPELL_AURA_REFRESH'
            || event.name === 'SPELL_AURA_APPLIED_DOSE'
        ) {
            const existing = entry.auras.get(event.spellId)
            if (existing) {
                existing.stacks = event.stacks || existing.stacks
                if (event.name === 'SPELL_AURA_APPLIED') existing.appliedAt = event.ts
            } else {
                entry.auras.set(event.spellId, {
                    spellId: event.spellId,
                    name: event.spellName,
                    auraType: event.auraType,
                    stacks: event.stacks || 1,
                    appliedAt: event.ts,
                })
            }
        } else if (event.name === 'SPELL_AURA_REMOVED_DOSE') {
            const existing = entry.auras.get(event.spellId)
            if (existing) existing.stacks = event.stacks
        }

        if (dest.isGroupMember) {
            activeSegments().forEach((segment) => applyAura(segment, event, dest))
        }
    }

    function handleEvent(event) {
        state.eventCount += 1
        if (!state.firstTs) {
            state.firstTs = event.ts
            logSegment.startTs = event.ts
            logSegment.timeline.startTs = event.ts
        }
        state.lastTs = event.ts
        if (state.zone) state.zone.events += 1

        if (event.category === 'meta') {
            handleMeta(event)
            return
        }

        registry.observe(event.sourceGuid, event.sourceName, event.sourceFlags)
        registry.observe(event.destGuid, event.destName, event.destFlags)

        if (event.hasAdvanced && event.ownerGuid && event.ownerGuid !== '0000000000000000') {
            registry.setOwner(event.infoGuid, event.ownerGuid)
        }
        if (event.category === 'summon') {
            registry.setOwner(event.destGuid, event.sourceGuid)
        }

        updateHp(event)

        if (event.name === 'SWING_DAMAGE' || event.name === 'SWING_DAMAGE_LANDED') {
            pruneSwingPairs(event.ts)
            if (isDuplicateSwing(event)) {
                // The duplicate still carries the victim's health, which the
                // death timeline needs, so it updates the track and stops there.
                trackPlayerEvent(event)
                return
            }
        }

        if (event.category === 'aura') {
            trackAura(event)
            return
        }

        trackPlayerEvent(event)

        if (event.category === 'death') {
            const dest = registry.get(event.destGuid)
            if (dest && dest.isPlayer) {
                recordDeath(event, dest)
            } else if (dest) {
                state.enemyDeaths += 1
                activeSegments().forEach((segment) => {
                    segment.enemyDeaths += 1
                })
            }
            return
        }

        if (event.category === 'other') {
            if (state.unknownEvents.size < MAX_UNKNOWN_EVENTS) {
                state.unknownEvents.set(event.name, (state.unknownEvents.get(event.name) || 0) + 1)
            }
            return
        }

        const segments = activeSegments()
        for (let index = 0; index < segments.length; index += 1) {
            applyEvent(segments[index], event, { registry })
        }
    }

    return {
        state,
        registry,
        get dateOrder() {
            return parser.dateOrder
        },
        get logSegment() {
            return logSegment
        },
        get finishedSegments() {
            return finishedSegments
        },
        get openSegments() {
            return [activeChallenge, activeEncounter].filter(Boolean)
        },

        // Feeds one decoded chunk of complete lines. Returns the number of lines
        // consumed. A partial trailing line must never reach this method: the
        // reader cuts chunks on newline boundaries.
        processLines(text) {
            let start = 0
            let lines = 0
            const length = text.length

            while (start < length) {
                let end = text.indexOf('\n', start)
                if (end < 0) end = length
                if (end > start) {
                    const line = text.charCodeAt(end - 1) === 13
                        ? text.slice(start, end - 1)
                        : text.slice(start, end)
                    state.lineCount += 1
                    lines += 1
                    if (line.length > 8) {
                        try {
                            const event = parser.parse(line)
                            if (event) handleEvent(event)
                            else state.skippedLines += 1
                        } catch (error) {
                            state.parseErrors += 1
                        }
                    } else {
                        state.skippedLines += 1
                    }
                }
                start = end + 1
            }

            if (!state.dateOrder) state.dateOrder = parser.dateOrder
            return lines
        },

        // Closes anything still open so a completed file reports final numbers.
        // Open segments are kept separate from finished ones: an append to the
        // same file can continue them later.
        sealForReport() {
            closeOpenAuras(logSegment, state.lastTs)
        },

        serialize() {
            return {
                version: STATE_VERSION,
                state: {
                    ...state,
                    deaths: state.deaths,
                    unknownEvents: state.unknownEvents,
                },
                registry: registry.serialize(),
                logSegment,
                finishedSegments,
                activeChallenge,
                activeEncounter,
                tracks,
                dedupe: { pendingSwing, pendingLanded, prunedAt: dedupePrunedAt },
            }
        },

        restore(snapshot) {
            if (!snapshot || snapshot.version !== STATE_VERSION) return false
            Object.assign(state, snapshot.state)
            logSegment = snapshot.logSegment
            finishedSegments = snapshot.finishedSegments || []
            activeChallenge = snapshot.activeChallenge || null
            activeEncounter = snapshot.activeEncounter || null
            tracks.clear()
            if (snapshot.tracks instanceof Map) {
                snapshot.tracks.forEach((value, key) => tracks.set(key, value))
            }
            if (snapshot.dedupe) {
                pendingSwing = snapshot.dedupe.pendingSwing || new Map()
                pendingLanded = snapshot.dedupe.pendingLanded || new Map()
                dedupePrunedAt = snapshot.dedupe.prunedAt || 0
            }
            if (state.dateOrder) parser.setDateOrder(state.dateOrder)
            return true
        },
    }
}

export function restoreAnalyzer(snapshot) {
    if (!snapshot || snapshot.version !== STATE_VERSION) return null
    const analyzer = createAnalyzer({
        fileName: snapshot.state && snapshot.state.fileName,
        registry: snapshot.registry,
        dateOrder: snapshot.state && snapshot.state.dateOrder,
    })
    return analyzer.restore(snapshot) ? analyzer : null
}
